import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import 'katex/dist/katex.min.css';
import type { ParagraphBlock } from '@/types/contentBlocks';
import {
  CitationSource,
  replaceCitationsWithFavicons,
  citationBadgeStyles,
} from '@/utils/citationReplacer';

interface ParagraphBlockProps {
  block: ParagraphBlock;
  isDarkMode: boolean;
  onLinkClick?: (rawUrl: string) => void;
  citationSources?: CitationSource[];
}

/* ----------------------- Utilities ----------------------- */

const getHighQualityFavicon = (rawUrl: string) => {
  try {
    const { hostname } = new URL(rawUrl);

    // Google favicons are transparent when source icon is
    return `https://www.google.com/s2/favicons?domain=${hostname}&sz=128`;
  } catch {
    return '';
  }
};

const getDirectFaviconFallback = (rawUrl: string) => {
  try {
    const { hostname } = new URL(rawUrl);
    return `https://${hostname}/favicon.ico`;
  } catch {
    return '';
  }
};

/* ----------------------- Component ----------------------- */

export const ParagraphBlockRenderer: React.FC<ParagraphBlockProps> = ({
  block,
  isDarkMode,
  onLinkClick,
  citationSources,
}) => {
  const sanitizedContent = React.useMemo(() => {
    const unicodeSpaceRegex = /[\u00A0\u2000-\u200B\u202F]/g;
    let base = (block.content || '').replace(unicodeSpaceRegex, ' ');

    if (citationSources && citationSources.length > 0) {
      base = replaceCitationsWithFavicons(base, citationSources);
    } else {
      // Only remove fullwidth brackets (often used for citations in some models)
      // Do NOT remove standard brackets as they break markdown links
      base = base.replace(/[【】]/g, '');
    }

    return base;
  }, [block.content, citationSources]);

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: citationBadgeStyles }} />
      <div className={`prose max-w-none text-base ${isDarkMode ? 'prose-invert' : ''}`}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm, remarkMath]}
          rehypePlugins={[
            rehypeRaw,
            [
              rehypeKatex,
              {
                strict: false,
                displayMode: false,
                errorColor: isDarkMode ? '#ff9999' : '#cc0000',
                trust: true,
                throwOnError: false,
              },
            ],
          ]}
          components={{
            a: ({ href, children }) => {
              if (!href) return <>{children}</>;

              let finalHref = href;
              // If it lacks protocol and doesn't start with /, treat as external if it looks like a domain
              // or just prepend https:// to be safe for non-relative paths
              if (
                !finalHref.startsWith('http://') &&
                !finalHref.startsWith('https://') &&
                !finalHref.startsWith('/') &&
                !finalHref.startsWith('#') &&
                !finalHref.startsWith('mailto:')
              ) {
                // Simple heuristic: if it has a dot and no spaces, assume it's a URL
                if (finalHref.includes('.') && !finalHref.includes(' ')) {
                  finalHref = `https://${finalHref}`;
                }
              }

              const isExternal =
                finalHref.startsWith('http://') || finalHref.startsWith('https://');

              if (isExternal) {
                const favicon = getHighQualityFavicon(finalHref);

                return (
                  <a
                    href={finalHref}
                    className="inline-flex items-center gap-1 rounded-full p-1 transition-colors bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10"
                    title={finalHref}
                    onClick={(e) => {
                      if (onLinkClick) {
                        e.preventDefault();
                        onLinkClick(finalHref);
                      }
                    }}
                  >
                    {favicon && (
                      <img
                        src={favicon}
                        alt=""
                        className="inline-block w-4 h-4 rounded-full object-contain"
                        loading="lazy"
                        onError={(e) => {
                          const img = e.currentTarget;
                          const fallback = getDirectFaviconFallback(finalHref);
                          if (fallback && img.src !== fallback) {
                            img.src = fallback;
                          } else {
                            img.style.display = 'none';
                          }
                        }}
                      />
                    )}

                    {/* citation-style: hide visible URL text */}
                    <span className="sr-only">{finalHref}</span>
                  </a>
                );
              }

              // Internal links
              return (
                <a
                  href={finalHref}
                  className="text-blue-600 dark:text-blue-400 hover:underline transition-colors font-medium inline-flex items-center gap-1"
                >
                  {children}
                </a>
              );
            },

            code: ({ children }) => {
              const content = String(children);
              if (content.startsWith('$') && content.endsWith('$')) {
                return <span className="inline-math">{content}</span>;
              }
              return (
                <code
                  className={`rounded-md px-2 py-[2px] text-sm font-mono ${isDarkMode
                    ? 'bg-[#292929] text-gray-300'
                    : 'bg-[#f0f0f0] text-[#292929]'
                    }`}
                >
                  {children}
                </code>
              );
            },
          }}
        >
          {sanitizedContent}
        </ReactMarkdown>
      </div>
    </>
  );
};
